"use client";

import { useMemo, useState, type FormEvent } from "react";
import {
  Button,
  Card,
  Icon,
  IconButton,
  Input,
  Label,
  MapPin,
  Plus,
  Search,
  Spinner,
  Trash2,
} from "@/components";
import type { Point } from "@/lib/map";
import { placeLabel } from "@/lib/map";

/** A geocoded candidate the user can confirm onto the map. */
export interface PlacePreview {
  /** The address/name the user typed — the row identity + address-column value. */
  query: string;
  /** The provider's normalized address (or the query when none), shown in the preview. */
  label: string;
  lat: number;
  lng: number;
  confidence: number;
  needsReview: boolean;
}

export interface PlacesPanelProps {
  points: Point[];
  /** Popup title column — drives each row's display label. */
  titleColumn?: string;
  /** True when the map is at its point ceiling — the add form is disabled. */
  atCeiling: boolean;
  /** Unique per rendered instance (side panel vs drawer) so ids never collide. */
  idPrefix: string;
  /** The drawer supplies its own heading — hide the panel's. */
  embedded?: boolean;
  /** Geocode the typed address; resolves to a candidate, or null when unmatched. */
  onFind: (query: string) => Promise<PlacePreview | null>;
  /** Confirm the previewed candidate onto the map. */
  onAdd: (preview: PlacePreview) => void;
  /** Remove the point with this id from the map. */
  onRemove: (id: string) => void;
}

/** Real-time, client-only filtering over the points already on this map. */
export function filterPlaces(points: Point[], titleColumn: string | undefined, query: string): Point[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === "") return points;
  return points.filter((point) => {
    const searchable = [
      placeLabel(point, titleColumn),
      ...Object.values(point.props),
      point.lat.toFixed(4),
      point.lng.toFixed(4),
    ];
    return searchable.some((value) => value.toLocaleLowerCase().includes(needle));
  });
}

/**
 * Places panel (MAP-7) — searching the current map and adding a new place are
 * deliberately separate. The always-visible search filters existing points in
 * real time. "Add a place" reveals the geocoder-backed lookup and confirmation
 * flow. The editor still owns geocoding + PointSet mutation.
 */
export function PlacesPanel({
  points,
  titleColumn,
  atCeiling,
  idPrefix,
  embedded = false,
  onFind,
  onAdd,
  onRemove,
}: PlacesPanelProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [finding, setFinding] = useState(false);
  const [preview, setPreview] = useState<PlacePreview | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchInputId = `${idPrefix}-place-search`;
  const addInputId = `${idPrefix}-place-add`;
  const trimmed = addQuery.trim();
  const filteredPoints = useMemo(
    () => filterPlaces(points, titleColumn, searchQuery),
    [points, titleColumn, searchQuery],
  );

  function reset() {
    setPreview(null);
    setNotFound(false);
    setError(null);
  }

  async function handleFind(event: FormEvent) {
    event.preventDefault();
    if (trimmed === "" || finding || atCeiling) return;
    setFinding(true);
    setPreview(null);
    setNotFound(false);
    setError(null);
    try {
      const result = await onFind(trimmed);
      if (result) setPreview(result);
      else setNotFound(true);
    } catch {
      setError("Could not look up that address. Try again in a moment.");
    } finally {
      setFinding(false);
    }
  }

  function handleAdd() {
    if (!preview) return;
    onAdd(preview);
    setAddQuery("");
    setAdding(false);
    reset();
  }

  return (
    <Card className="places-panel">
      {!embedded && <h2 className="places-panel__title">Places</h2>}
      <p className="places-panel__count">
        {points.length.toLocaleString()} {points.length === 1 ? "place" : "places"} on the map
      </p>

      <div className="places-search">
        <Label htmlFor={searchInputId}>Search places on this map</Label>
        <div className="places-search__input">
          <Icon icon={Search} size="sm" />
          <Input
            id={searchInputId}
            value={searchQuery}
            placeholder="Search names or addresses"
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
      </div>

      {!adding ? (
        <>
          <Button
            variant="secondary"
            size="sm"
            iconLeading={<Icon icon={Plus} size="sm" />}
            disabled={atCeiling}
            onClick={() => setAdding(true)}
          >
            Add a place
          </Button>
          {atCeiling ? (
            <p className="places-add__note">This map is at its point limit. Remove a place to add another.</p>
          ) : null}
        </>
      ) : (
        <form className="places-add" onSubmit={handleFind}>
          <div className="places-add__heading">
            <Label htmlFor={addInputId}>Address or place name</Label>
            <button
              type="button"
              className="places-add__close"
              onClick={() => {
                setAdding(false);
                setAddQuery("");
                reset();
              }}
            >
              Cancel
            </button>
          </div>
          <div className="places-add__row">
            <Input
              id={addInputId}
              value={addQuery}
              placeholder="Enter an address or place name"
              disabled={atCeiling}
              onChange={(event) => {
                setAddQuery(event.target.value);
                if (preview || notFound || error) reset();
              }}
            />
            <Button
              type="submit"
              variant="secondary"
              size="md"
              disabled={trimmed === "" || atCeiling}
              loading={finding}
              iconLeading={<Icon icon={Search} size="sm" />}
            >
              Find location
            </Button>
          </div>
          {atCeiling && (
            <p className="places-add__note">This map is at its point limit. Remove a place to add another.</p>
          )}
          {notFound && (
            <p className="places-add__note">No match for that address. Check the spelling, or add more detail.</p>
          )}
          {error && <p className="places-add__note places-add__note--error">{error}</p>}
        </form>
      )}

      {preview && (
        <div className="places-preview" aria-live="polite">
          <div className="places-preview__body">
            <span className="places-preview__pin">
              <Icon icon={MapPin} size="sm" />
            </span>
            <div>
              <p className="places-preview__label">{preview.label}</p>
              <p className="places-preview__coords">
                {preview.lat.toFixed(5)}, {preview.lng.toFixed(5)}
                {preview.needsReview ? " · low confidence" : ""}
              </p>
            </div>
          </div>
          <div className="places-preview__actions">
            <Button variant="ghost" size="sm" onClick={reset}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              iconLeading={<Icon icon={Plus} size="sm" />}
              onClick={handleAdd}
            >
              Add to map
            </Button>
          </div>
        </div>
      )}

      <div className="places-list">
        {points.length === 0 ? (
          <p className="places-list__empty">
            {finding ? <Spinner size="sm" /> : "No points yet. Add one above."}
          </p>
        ) : filteredPoints.length === 0 ? (
          <p className="places-list__empty">No places match “{searchQuery.trim()}”.</p>
        ) : (
          <ul className="places-list__items">
            {filteredPoints.map((p) => {
              const label = placeLabel(p, titleColumn);
              return (
                <li key={p.id} className="places-list__item">
                  <div className="places-list__text">
                    <p className="places-list__label">{label}</p>
                    <p className="places-list__coords">
                      {p.lat.toFixed(4)}, {p.lng.toFixed(4)}
                    </p>
                  </div>
                  <IconButton
                    label={`Remove ${label}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(p.id)}
                  >
                    <Icon icon={Trash2} size="sm" />
                  </IconButton>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
